import { type ReactNode, useRef, useState } from "react";

/**
 * A document shown while the pointer rests on its button (D, 8 October 2026: "instead of
 * click and click again to open the PDF"). A PDF is shown in a frame, a photo as itself.
 * The button underneath still opens it in its own tab when clicked, which is also how an
 * iPad, with no pointer to rest, sees it. A short wait keeps it from flashing up while the
 * pointer only passes over the row.
 */
export function HoverPreview({
  src,
  kind,
  content,
  children,
}: {
  /** Nothing to show: the button works as it did, with no preview. */
  readonly src: string | null;
  readonly kind: "pdf" | "image";
  /**
   * Shown instead when there is no document: the items keyed by hand (D, 15 October 2026:
   * "for those that don't have invoice, I will manually key in. Can you also display them
   * when I hover my mouse over?").
   */
  readonly content?: ReactNode;
  readonly children: ReactNode;
}): React.ReactElement {
  const [at, setAt] = useState<{ left: number; top: number } | undefined>();
  const timer = useRef<number | undefined>(undefined);

  if (!src && !content) return <>{children}</>;

  const WIDTH = 460;
  const HEIGHT = src ? 600 : 360;
  return (
    <span
      className="inline-flex"
      onMouseEnter={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          // To the left of the button, which sits at the right of its row, and on screen.
          const left = Math.max(8, box.left - WIDTH - 12);
          const top = Math.max(8, Math.min(box.top - HEIGHT / 3, window.innerHeight - HEIGHT - 8));
          setAt({ left, top });
        }, 250);
      }}
      onMouseLeave={() => {
        window.clearTimeout(timer.current);
        setAt(undefined);
      }}
    >
      {children}
      {at ? (
        <span
          className={`fixed z-[200] rounded-md border border-defaultborder bg-white dark:bg-bodybg shadow-lg overflow-hidden pointer-events-none ${src ? "" : "p-3 text-start whitespace-normal"}`}
          style={src ? { left: at.left, top: at.top, width: WIDTH, height: HEIGHT } : { left: at.left, top: at.top, width: WIDTH, maxHeight: window.innerHeight - 16 }}
        >
          {!src ? (
            content
          ) : kind === "pdf" ? (
            <iframe title="Preview" src={`${src}#toolbar=0&navpanes=0&view=FitH`} className="w-full h-full border-0" />
          ) : (
            <img src={src} alt="" className="w-full h-full object-contain" />
          )}
        </span>
      ) : null}
    </span>
  );
}
